import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { FilterAltOff as FilterAltOffIcon } from '@mui/icons-material';
import type { FC } from 'react';
import { memo, Suspense, useCallback, useEffect, useMemo } from 'react';
import CompanyAutocomplete from './CompanyAutocomplete';
import PageNameAutocomplete from './PageNameAutocomplete';
import BranchesAutocomplete from './BranchesAutucomplete';
import DomainFlowTreeTrigger from './DomainFlowTreeTrigger';
import MondaysDatePickers from './MondaysDatePickers';
import CreativePreviewMode from './CreativePreviewMode';
import type { AdType, GoogleAdType } from '../types';
import type { OptionValue } from '../../../components/common/CheckboxAutocomplete';
import CheckboxAutocomplete from '../../../components/common/CheckboxAutocomplete';
import { AD_TYPES_OPTIONS, SORTING_OPTIONS, SORTING_OPTIONS_GOOGLE_US, SORTING_OPTIONS_US } from '../constants';
import type { SortingOption } from '../constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getPageCompanyBranchRelationsOptions, getAdvertiserCompanyDomainRelationsOptions, getDomainFlowRelationsOptions } from '../api';
import { CompanyBiMap } from '../utils/CompanyBiMap';
import { FlowBiMap } from '../utils/FlowBiMap';
import { GoogleAdvertiserBiMap } from '../utils/GoogleAdvertiserBiMap';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';
import { trackAdsLibraryNetworkChange, trackAdsLibraryRegionChange } from 'lib/datadog';
import type { AdsLibraryNetwork, AdsLibraryRegion } from 'lib/datadog';
import GoogleCompanyAutocomplete from './GoogleCompanyAutocomplete';
import GoogleDomainAutocomplete from './GoogleDomainAutocomplete';
import GoogleAdvertiserAutocomplete from './GoogleAdvertiserAutocomplete';
import IconMetaFacebook from 'assets/icon-meta-facebook.svg?react';
import IconGoogleAds from 'assets/icon-google-ads.svg?react';
import IconRegionEu from 'assets/icon-region-eu.svg?react';
import IconRegionUs from 'assets/icon-region-us.svg?react';

interface FbAdsFilterBarProps {
  onReset: () => void;
}

const ROW_WRAPPER_SX = {
  background: '#fff',
  boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
  p: '12px',
  borderRadius: '8px',
};

/* ── Data-dependent filters (wrapped in Suspense) ── */

interface DataFiltersProps {
  network: string;
  region: string;
}

interface SortBySelectProps {
  ordering: string;
  sortingOptions: SortingOption[];
  onChange: (event: SelectChangeEvent) => void;
}

interface RunningFilterRowProps {
  isRunning: boolean;
  setIsRunning: (value: boolean) => void;
}

interface NewOnlyFilterRowProps {
  newOnly: boolean;
  setNewOnly: (value: boolean) => void;
}

interface IncludeWelltechFilterRowProps {
  includeWelltech: boolean;
  setIncludeWelltech: (value: boolean) => void;
}

interface LastShownRecentlyFilterRowProps {
  lastShownRecently: boolean;
  setLastShownRecently: (value: boolean) => void;
}

const SortBySelect: FC<SortBySelectProps> = ({ ordering, sortingOptions, onChange }) => (
  <FormControl size="small" sx={{ width: 160, minWidth: 0, flexShrink: 0 }}>
    <InputLabel id="sort">Sort by</InputLabel>
    <Select
      id="sort-list"
      labelId="sort"
      label="Sort by"
      value={ordering}
      onChange={onChange}
      renderValue={(selected) => (
        <Box
          component="span"
          sx={{
            display: 'block',
            width: '80%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {sortingOptions.find(([value]) => value === selected)?.[1] ?? String(selected)}
        </Box>
      )}
      SelectDisplayProps={{
        style: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
        },
      }}
      sx={{
        '& .MuiSelect-select': {
          pr: 4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          minWidth: 0,
        },
      }}
    >
      {sortingOptions.map(([value, label]) => (
        <MenuItem
          key={value}
          value={value}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'grey.100',
            },
            '&.Mui-selected:hover': {
              backgroundColor: 'grey.100',
            },
          }}
        >
          {label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const NewOnlyFilterRow: FC<NewOnlyFilterRowProps> = ({ newOnly, setNewOnly }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, pl: 0 }}>
    <FormControlLabel
      id="new-only-label"
      control={
        <Checkbox
          id="new-only-checkbox"
          size="small"
          checked={newOnly}
          onChange={(event) => {
            setNewOnly(event.target.checked);
          }}
          name="new_only"
        />
      }
      label="New creatives only"
      sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
    />
  </Box>
);

const IncludeWelltechFilterRow: FC<IncludeWelltechFilterRowProps> = ({
  includeWelltech,
  setIncludeWelltech,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, pl: 0 }}>
    <FormControlLabel
      id="include-welltech-label"
      control={
        <Checkbox
          id="include-welltech-checkbox"
          size="small"
          checked={includeWelltech}
          onChange={(event) => {
            setIncludeWelltech(event.target.checked);
          }}
          name="include_welltech"
        />
      }
      label="Include Welltech creatives"
      sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
    />
  </Box>
);

const LastShownRecentlyFilterRow: FC<LastShownRecentlyFilterRowProps> = ({
  lastShownRecently,
  setLastShownRecently,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, pl: 0 }}>
    <FormControlLabel
      id="last-shown-recently-label"
      control={
        <Checkbox
          id="last-shown-recently-checkbox"
          size="small"
          checked={lastShownRecently}
          onChange={(event) => {
            setLastShownRecently(event.target.checked);
          }}
          name="last_shown_recently"
        />
      }
      label="Last shown recently"
      sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
    />
  </Box>
);

const RunningFilterRow: FC<RunningFilterRowProps> = ({ isRunning, setIsRunning }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, pl: 0 }}>
    <FormControlLabel
      id="running-label"
      control={
        <Checkbox
          id="running-checkbox"
          size="small"
          checked={isRunning}
          onChange={(event) => {
            setIsRunning(event.target.checked);
          }}
          name="running"
        />
      }
      label="Active (Last sync)"
      sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
    />
  </Box>
);

const DataFilters: FC<DataFiltersProps> = ({ network, region }) => {
  const {
    actualCompany,
    setCompany,
    setBranches,
    actualBranches,
    actualDomains,
    setDomains,
    actualFlows,
    setFlows,
    setPageName,
    actualPageName,
    actualAdTypes,
    setAdTypes,
  } = useFacebookAdsLibraryParams();

  const { data: rawCompanyData } = useSuspenseQuery(getPageCompanyBranchRelationsOptions({ network, region }));
  const biMap = useMemo(() => new CompanyBiMap(rawCompanyData), [rawCompanyData]);

  const { data: rawFlowData } = useSuspenseQuery(getDomainFlowRelationsOptions({ network, region }));
  const flowBiMap = useMemo(() => new FlowBiMap(rawFlowData), [rawFlowData]);

  // Cascading options: each is synchronously derived from current URL params
  const availableBranchIds = useMemo(
    () => biMap.getForCompanies(actualCompany).branchIds,
    [biMap, actualCompany]
  );
  const availableCompanyIds = useMemo(
    () => biMap.getForBranches(actualBranches).companyIds,
    [biMap, actualBranches]
  );
  const availablePageIds = useMemo(
    () => biMap.getForSelection(actualCompany, actualBranches).pageIds,
    [biMap, actualCompany, actualBranches]
  );
  const availableDomains = useMemo(
    () => flowBiMap.getForSelection(actualCompany, actualBranches).domains,
    [flowBiMap, actualCompany, actualBranches]
  );
  const availableFlows = useMemo(
    () =>
      actualDomains.length === 0
        ? []
        : flowBiMap.getForSelection(actualCompany, actualBranches, actualDomains).flows,
    [flowBiMap, actualCompany, actualBranches, actualDomains]
  );

  // Prune selected domains/flows that no longer exist under the current company/branch selection
  useEffect(() => {
    if (actualDomains.length === 0) return;
    const valid = actualDomains.filter((d) => availableDomains.includes(d));
    if (valid.length !== actualDomains.length) setDomains(valid);
  }, [availableDomains, actualDomains, setDomains]);

  useEffect(() => {
    if (actualFlows.length === 0) return;
    const valid = actualFlows.filter((f) => availableFlows.includes(f));
    if (valid.length !== actualFlows.length) setFlows(valid);
  }, [availableFlows, actualFlows, setFlows]);

  const handleBranchChange = useCallback((br: number[]) => { setBranches(br); }, [setBranches]);
  const handleCompanyChange = useCallback((comp: number[]) => { setCompany(comp); }, [setCompany]);
  const handlePageNameChange = useCallback((pn: number[]) => { setPageName(pn); }, [setPageName]);
  const handleAdTypeChange = useCallback((at: AdType[]) => { setAdTypes(at); }, [setAdTypes]);

  const handleTreeApply = useCallback(
    (domains: string[], flows: string[]) => {
      setDomains(domains);
      setFlows(flows);
    },
    [setDomains, setFlows]
  );

  return (
    <>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Suspense fallback={<Skeleton variant="rounded" width="100%" height={36} />}>
          <BranchesAutocomplete
            allowedValues={availableBranchIds}
            size="small"
            value={actualBranches}
            InputProps={{ label: 'Branches' }}
            sx={{ width: '100%', minWidth: 120 }}
            onChange={handleBranchChange}
          />
        </Suspense>
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Suspense fallback={<Skeleton variant="rounded" width="100%" height={36} />}>
          <CompanyAutocomplete
            allowedValues={availableCompanyIds}
            size="small"
            value={actualCompany}
            InputProps={{ label: 'Company' }}
            sx={{ width: '100%', minWidth: 120 }}
            onChange={handleCompanyChange}
          />
        </Suspense>
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Suspense fallback={<Skeleton variant="rounded" width="100%" height={36} />}>
          <PageNameAutocomplete
            allowedValues={availablePageIds}
            size="small"
            value={actualPageName}
            InputProps={{ label: 'Page name' }}
            sx={{ width: '100%' }}
            onChange={handlePageNameChange}
          />
        </Suspense>
      </Box>
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <CheckboxAutocomplete
          id="fb-ad-types-autocomplete"
          options={AD_TYPES_OPTIONS}
          size="small"
          value={actualAdTypes}
          InputProps={{ label: 'Ad type' }}
          onChange={handleAdTypeChange as (value: OptionValue[]) => void}
        />
      </Box>
      <DomainFlowTreeTrigger
        flowBiMap={flowBiMap}
        companyIds={actualCompany}
        branchIds={actualBranches}
        selectedFlows={actualFlows}
        onApply={handleTreeApply}
      />
    </>
  );
};

/* ── Google US filters: Company ↔ Domain ↔ Advertiser (bidirectional relation graph) ── */

const GOOGLE_AD_TYPES_OPTIONS: { value: GoogleAdType; label: string }[] = [
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Image' },
  { value: 'text', label: 'Text' },
];

const GoogleDataFilters: FC = () => {
  const {
    network,
    region,
    actualCompany,
    setCompany,
    actualDomains,
    setDomains,
    actualAdvertiserIds,
    setAdvertiserIds,
    actualGoogleAdTypes,
    setGoogleAdTypes,
  } = useFacebookAdsLibraryParams();

  const { data: rawRelationData } = useSuspenseQuery(
    getAdvertiserCompanyDomainRelationsOptions({ network, region })
  );
  const filterBiMap = useMemo(() => new GoogleAdvertiserBiMap(rawRelationData), [rawRelationData]);

  // Bidirectional cascade: each filter's options are narrowed by the other two selections
  const availableCompanyIds = useMemo(
    () => filterBiMap.getForSelection([], actualDomains, actualAdvertiserIds).companyIds,
    [filterBiMap, actualDomains, actualAdvertiserIds]
  );

  const availableDomains = useMemo(
    () => filterBiMap.getForSelection(actualCompany, [], actualAdvertiserIds).domains,
    [filterBiMap, actualCompany, actualAdvertiserIds]
  );

  const availableAdvertiserIds = useMemo(
    () => filterBiMap.getForSelection(actualCompany, actualDomains, []).advertiserIds,
    [filterBiMap, actualCompany, actualDomains]
  );

  useEffect(() => {
    if (actualCompany.length === 0) return;
    const valid = actualCompany.filter((id) => availableCompanyIds.includes(id));
    if (valid.length !== actualCompany.length) setCompany(valid);
  }, [availableCompanyIds, actualCompany, setCompany]);

  useEffect(() => {
    if (actualDomains.length === 0) return;
    const valid = actualDomains.filter((d) => availableDomains.includes(d));
    if (valid.length !== actualDomains.length) setDomains(valid);
  }, [availableDomains, actualDomains, setDomains]);

  useEffect(() => {
    if (actualAdvertiserIds.length === 0) return;
    const valid = actualAdvertiserIds.filter((id) => availableAdvertiserIds.includes(id));
    if (valid.length !== actualAdvertiserIds.length) setAdvertiserIds(valid);
  }, [availableAdvertiserIds, actualAdvertiserIds, setAdvertiserIds]);

  const handleCompanyChange = useCallback(
    (comp: number[]) => { setCompany(comp); },
    [setCompany]
  );
  const handleDomainChange = useCallback(
    (domains: string[]) => { setDomains(domains); },
    [setDomains]
  );
  const handleAdvertiserChange = useCallback(
    (ids: string[]) => { setAdvertiserIds(ids); },
    [setAdvertiserIds]
  );
  const handleGoogleAdTypeChange = useCallback(
    (types: GoogleAdType[]) => { setGoogleAdTypes(types); },
    [setGoogleAdTypes]
  );

  return (
    <>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Suspense fallback={<Skeleton variant="rounded" width="100%" height={36} />}>
          <GoogleCompanyAutocomplete
            allowedValues={availableCompanyIds}
            size="small"
            value={actualCompany}
            InputProps={{ label: 'Company' }}
            sx={{ width: '100%', minWidth: 120 }}
            onChange={handleCompanyChange}
          />
        </Suspense>
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <GoogleDomainAutocomplete
          allowedDomains={availableDomains}
          size="small"
          value={actualDomains}
          InputProps={{ label: 'Domain' }}
          sx={{ width: '100%', minWidth: 120 }}
          onChange={handleDomainChange}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Suspense fallback={<Skeleton variant="rounded" width="100%" height={36} />}>
          <GoogleAdvertiserAutocomplete
            allowedValues={availableAdvertiserIds}
            size="small"
            value={actualAdvertiserIds}
            InputProps={{ label: 'Advertiser ID' }}
            sx={{ width: '100%', minWidth: 120 }}
            onChange={handleAdvertiserChange}
          />
        </Suspense>
      </Box>
      <Box sx={{ flex: 1, minWidth: 140 }}>
        <CheckboxAutocomplete
          id="google-ad-types-autocomplete"
          options={GOOGLE_AD_TYPES_OPTIONS}
          size="small"
          value={actualGoogleAdTypes}
          InputProps={{ label: 'Ad type' }}
          onChange={handleGoogleAdTypeChange as (value: OptionValue[]) => void}
        />
      </Box>
    </>
  );
};

interface DataFiltersFallbackProps {
  isGoogleNetwork: boolean;
}

const DataFiltersFallback: FC<DataFiltersFallbackProps> = ({ isGoogleNetwork }) => (
  <>
    <Box sx={{ flex: 1, minWidth: 180 }}>
      <Skeleton variant="rounded" width="100%" height={36} />
    </Box>
    <Box sx={{ flex: 1, minWidth: 180 }}>
      <Skeleton variant="rounded" width="100%" height={36} />
    </Box>
    {isGoogleNetwork ? (
      <>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
      </>
    ) : (
      <>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 180 }}>
          <Skeleton variant="rounded" width="100%" height={36} />
        </Box>
        <Skeleton variant="rounded" width={160} height={36} />
      </>
    )}
  </>
);

/* ── Full filter bar ── */

const FbAdsFilterBar: FC<FbAdsFilterBarProps> = ({ onReset }) => {
  const {
    ordering,
    setOrdering,
    isRunning,
    setIsRunning,
    newOnly,
    setNewOnly,
    includeWelltech,
    setIncludeWelltech,
    lastShownRecently,
    setLastShownRecently,
    network,
    setNetworkWithScopedFilters,
    region,
    setRegionWithInitializedFilters,
  } = useFacebookAdsLibraryParams();
  const isGoogleNetwork = network === 'google';
  const isEuRegion = region === 'eu';
  const isUsRegion = region === 'us';
  const sortingOptions = isEuRegion ? SORTING_OPTIONS : isGoogleNetwork ? SORTING_OPTIONS_GOOGLE_US : SORTING_OPTIONS_US;
  const handleOrderingChange = useCallback(
    (event: SelectChangeEvent) => {
      setOrdering(event.target.value);
    },
    [setOrdering]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 3,
        py: 1.5,
      }}
    >
      {/* Row 1 */}
      <Box sx={{ ...ROW_WRAPPER_SX, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 2.5 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={network}
          onChange={(_, value: string | null) => {
            if (value !== 'facebook' && value !== 'google') return;
            trackAdsLibraryNetworkChange(value as AdsLibraryNetwork, network as AdsLibraryNetwork, region as AdsLibraryRegion);
            setNetworkWithScopedFilters(value);
          }}
          aria-label="Network"
        >
          <ToggleButton value="facebook">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconMetaFacebook width={16} height={16} />
              Facebook
            </Box>
          </ToggleButton>
          <ToggleButton value="google">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconGoogleAds width={16} height={16} />
              Google
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        <ToggleButtonGroup
          exclusive
          size="small"
          value={region}
          onChange={(_, value: string | null) => {
            if (value !== 'eu' && value !== 'us') return;
            trackAdsLibraryRegionChange(value as AdsLibraryRegion, network as AdsLibraryNetwork, region as AdsLibraryRegion);
            setRegionWithInitializedFilters(value);
          }}
          aria-label="Region"
        >
          {!isGoogleNetwork && (
            <ToggleButton value="eu">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <IconRegionEu width={16} height={16} />
                EU
              </Box>
            </ToggleButton>
          )}
          <ToggleButton value="us">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconRegionUs width={16} height={16} />
              US
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider orientation="vertical" flexItem sx={{ mx: 1.5 }} />

        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <MondaysDatePickers />

          {isEuRegion && <RunningFilterRow isRunning={!!isRunning} setIsRunning={setIsRunning} />}

          {isUsRegion && <NewOnlyFilterRow newOnly={newOnly} setNewOnly={setNewOnly} />}

          {isGoogleNetwork && isUsRegion && (
            <LastShownRecentlyFilterRow
              lastShownRecently={lastShownRecently}
              setLastShownRecently={setLastShownRecently}
            />
          )}

          <IncludeWelltechFilterRow
            includeWelltech={includeWelltech}
            setIncludeWelltech={setIncludeWelltech}
          />
        </Box>
      </Box>

      {/* Row 2 */}
      <Box sx={{ ...ROW_WRAPPER_SX, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 2 }}>
        <Suspense fallback={<DataFiltersFallback isGoogleNetwork={isGoogleNetwork} />}>
          {isGoogleNetwork ? (
            <GoogleDataFilters />
          ) : (
            <DataFilters network={network} region={region} />
          )}
        </Suspense>

        <Box sx={{ flex: 1 }} />

        <SortBySelect ordering={ordering} sortingOptions={sortingOptions} onChange={handleOrderingChange} />
        <Box sx={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 0.5, pl: 0 }}>
          <CreativePreviewMode />
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Tooltip title="Reset">
            <IconButton aria-label="Reset" onClick={onReset}>
              <FilterAltOffIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(FbAdsFilterBar);
